import { Injectable } from '@angular/core';
import { ApplicationApiClient } from '@nk/api-client-lib';
import { CachingService } from '@nk/caching-lib';
import { AppMembershipRoleCompositeEntity, AppRoleDims, AppRoleEntity, AppRoleKey } from '@nk/data-entities-lib';
import { arrayToPickyDict, arrayToPickyDictList, Dict, DictList, dictToKeys } from '@nk/utils-lib';
import { map, mergeMap, Observable, of } from 'rxjs';
import { AppService } from './app.service';

@Injectable({
  providedIn: 'root',
})
export class AppRoleService {
  constructor(
    private _appService: AppService,
    private _cachingService: CachingService,
    private _applicationApi: ApplicationApiClient,
  ) {}

  public appRoleListCache = this._cachingService.create(this, this.fetchAppRoleList);
  public fetchAppRoleList(): Observable<AppRoleEntity[]> {
    return this._applicationApi.get<AppRoleEntity[]>('app-role', {}, [], []);
  }

  // TODO: unit tests!
  public claimValidAppRoleKeys(roleKeys: AppRoleKey[] = [], appMembershipUuid?: string): Observable<AppRoleKey[]> {
    if (!roleKeys.length) {
      return of<AppRoleKey[]>([]);
    }
    const checkedRoleKeys = roleKeys.filter((key) => AppRoleKey[key]);
    return this.activeAppRoleDimsCache
      .get(appMembershipUuid)
      .pipe(map((roleDims) => checkedRoleKeys.filter((key) => roleDims.flags[key])));
  }

  public activeAppRoleDimsCache = this._cachingService.create(this, this.fetchActiveAppRoleDims);
  public fetchActiveAppRoleDims(appMembershipUuid?: string): Observable<AppRoleDims> {
    return this.appRoleListByMembershipCache.get(appMembershipUuid).pipe(map((roles) => new AppRoleDims(roles)));
  }

  // Not protected by feature
  public appRoleListByMembershipCache = this._cachingService.create(this, this.fetchAppRoleListByMembership);
  public fetchAppRoleListByMembership(appMembershipUuid?: string): Observable<AppRoleEntity[]> {
    if (appMembershipUuid) {
      return this._applicationApi.get<Required<AppRoleEntity>[]>(
        `app-membership/${appMembershipUuid}/app-role`,
        {},
        [],
        [],
      );
    } else {
      return this._appService.identityDataCache.get().pipe(
        mergeMap((identity) => {
          return this._applicationApi.get<AppRoleEntity[]>(
            `app-membership/${identity.app_membership.uuid}/app-role`,
            {},
            [],
            [],
          );
        }),
      );
    }
  }

  public allActiveAppRoleDimsCache = this._cachingService.create(this, this.fetchAllActiveAppRoleDims);
  public fetchAllActiveAppRoleDims(): Observable<Dict<AppRoleDims>> {
    return this.appMembershipRoleCompositeListByMembershipCache.get().pipe(
      map((appMembershipRoleCompositeList) => {
        const appRoleDictListByMembership: DictList<AppRoleEntity> = arrayToPickyDictList<
          AppMembershipRoleCompositeEntity,
          AppRoleEntity
        >(
          appMembershipRoleCompositeList,
          (m: AppMembershipRoleCompositeEntity) => m.app_membership.uuid,
          (m: AppMembershipRoleCompositeEntity) => m.app_role,
        );
        const roleDimPairs = dictToKeys(appRoleDictListByMembership).map((key: string) => {
          return {
            appMembershipUuid: key,
            appRoleDims: new AppRoleDims(appRoleDictListByMembership[key]),
          };
        });
        return arrayToPickyDict(
          roleDimPairs,
          (p) => p.appMembershipUuid,
          (p) => p.appRoleDims,
        );
      }),
    );
  }

  public appMembershipRoleCompositeListByMembershipCache = this._cachingService.create(
    this,
    this.fetchAppMembershipRoleCompositeListByMembership,
  );
  public fetchAppMembershipRoleCompositeListByMembership(): Observable<AppMembershipRoleCompositeEntity[]> {
    return this._applicationApi.get<AppMembershipRoleCompositeEntity[]>(`app-membership-role-composite`);
  }
}
